import { useState, useCallback, useRef, useMemo } from "react";
import { studentsApi, teachersApi, teacherLogsApi, timetableApi, attendanceApi, testsApi } from "../api";
import homeworkApi from "../api/homework";
import { muteToasts } from "../api/client";

/**
 * Centralises all initial data loading for the app.
 * Stable across renders — patchUser is consumed via ref so it never
 * invalidates the fetchAllData callback.
 */
export function useAppData(user, patchUser) {
  const [stuData, setStuData] = useState([]);
  const [teachData, setTeachData] = useState([]);
  const [curriculum, setCurriculum] = useState([]);
  const [tests, setTests] = useState([]);
  const [homework, setHomework] = useState([]);
  const [teacherLogs, setTeacherLogs] = useState([]);
  const [timetable, setTimetable] = useState({});
  const [dataLoaded, setDataLoaded] = useState(false);
  const [teachersLoading, setTeachersLoading] = useState(false);

  // Keep patchUser in a ref so fetchAllData doesn't re-create when it changes
  const patchUserRef = useRef(patchUser);
  patchUserRef.current = patchUser;

  // Prevent multiple simultaneous fetches
  const fetchInProgressRef = useRef(false);

  // Cache for API responses with timestamp and user ID
  const cacheRef = useRef({
    students: null,
    teachers: null,
    logs: null,
    timetable: null,
    attendance: null,
    timestamp: null,
    userId: null,
  });

  // Cache duration: 10 minutes for better performance
  const CACHE_DURATION = 10 * 60 * 1000;

  // Determine which data to fetch based on user role
  const requiredData = useMemo(() => {
    if (!user) return { students: false, teachers: false, logs: false, timetable: false, attendance: false };
    
    const role = user.role;
    
    // SaaS admin doesn't fetch school-specific data
    if (role === 'saas_admin') {
      return { students: false, teachers: false, logs: false, timetable: false, attendance: false };
    }
    
    const dataMap = {
      students: ['admin', 'teacher'].includes(role),
      teachers: ['admin', 'teacher', 'coordinator'].includes(role), // Allow teachers and coordinators to see staff directory
      logs: ['admin', 'teacher'].includes(role),
      timetable: ['admin', 'teacher', 'student'].includes(role),
      attendance: ['admin', 'teacher'].includes(role),
    };
    return dataMap;
  }, [user]);

  // Check if cache is valid AND belongs to current user
  const isCacheValid = useCallback(() => {
    const cache = cacheRef.current;
    return cache.timestamp &&
           cache.userId === user?._id &&
           (Date.now() - cache.timestamp < CACHE_DURATION);
  }, [CACHE_DURATION, user?._id]);

  const fetchAllData = useCallback(async () => {
    const startTime = performance.now();
    console.log("fetchAllData started at:", startTime);
    
    if (!user || fetchInProgressRef.current) return;
    
    fetchInProgressRef.current = true;
    setDataLoaded(false);
    muteToasts(true);

    const safeCall = (fn) => fn().catch(() => null);
    const cache = cacheRef.current;
    const useCache = isCacheValid();
    console.log("Cache check - useCache:", useCache, "cache age:", useCache ? Date.now() - cache.timestamp : 'N/A');

    try {
      // Build array of required API calls based on user role
      const apiCalls = [];
      const callKeys = [];
      
      if (requiredData.students) {
        if (useCache && cache.students) {
          apiCalls.push(Promise.resolve(cache.students));
        } else {
          const startTime = performance.now();
          // Fetch all students with pagination
          const fetchAllStudents = async () => {
            const allStudents = [];
            let page = 1;
            let hasMore = true;
            const pageSize = 200; // Backend limit

            while (hasMore && page <= 50) { // Max 50 pages (10,000 students)
              const result = await studentsApi.list({ limit: pageSize, page });
              console.log(`studentsApi.list page ${page} result:`, result);

              const students = result?.students || [];
              allStudents.push(...students);

              // Check if there are more pages
              const total = result?.total || result?.pagination?.total || 0;
              hasMore = students.length === pageSize && allStudents.length < total;

              console.log(`Page ${page}: fetched ${students.length}, total so far: ${allStudents.length}, hasMore: ${hasMore}`);

              if (students.length === 0) break;
              page++;
            }

            console.log(`studentsApi.list all pages completed in:`, performance.now() - startTime, `total students:`, allStudents.length);
            return { students: allStudents };
          };

          apiCalls.push(safeCall(fetchAllStudents));
        }
        callKeys.push('students');
      }
      
      if (requiredData.teachers) {
        const teachersStartTime = performance.now();
        if (useCache && cache.teachers) {
          console.log("Using cached teachers data");
          apiCalls.push(Promise.resolve(cache.teachers));
        } else {
          console.log("Making fresh teachers API call");
          const startTime = performance.now();
          apiCalls.push(
            safeCall(() => teachersApi.list())
              .then(result => {
                console.log(`teachersApi.list completed in:`, performance.now() - startTime);
                return result;
              })
          );
        }
        callKeys.push('teachers');
        console.log("Teachers setup time:", performance.now() - teachersStartTime);
      }
      
      if (requiredData.logs) {
        if (useCache && cache.logs) {
          apiCalls.push(Promise.resolve(cache.logs));
        } else {
          const startTime = performance.now();
          apiCalls.push(
            safeCall(() => teacherLogsApi.list())
              .then(result => {
                console.log(`teacherLogsApi.list completed in:`, performance.now() - startTime);
                return result;
              })
          );
        }
        callKeys.push('logs');
      }
      
      if (requiredData.timetable) {
        if (useCache && cache.timetable) {
          apiCalls.push(Promise.resolve(cache.timetable));
        } else {
          const startTime = performance.now();
          apiCalls.push(
            safeCall(() => timetableApi.get())
              .then(result => {
                console.log(`timetableApi.get completed in:`, performance.now() - startTime);
                return result;
              })
          );
        }
        callKeys.push('timetable');
      }

      // Fetch tests + homework for students
      if (user?.role === 'student') {
        const startTime = performance.now();
        const schoolId = user?.school?._id || user?.schoolId;
        const studentClass = user?.class || user?.student?.class;
        const extractGrade = (className) => {
          if (!className) return "";
          const match = className.match(/(\d+)/);
          return match ? match[1] : className;
        };
        const extractSection = (className) => {
          if (!className) return "";
          const match = className.match(/[a-zA-Z]+$/);
          return match ? match[0].toUpperCase() : "";
        };
        const grade = extractGrade(studentClass);
        const section = user?.section || extractSection(studentClass);
        const testParams = { schoolId };
        if (grade) testParams.class = grade;
        if (section) testParams.section = section;

        console.log("[DEBUG] Student tests fetch params:", testParams);
        apiCalls.push(
          safeCall(() => testsApi.list(testParams))
            .then(result => {
              console.log(`[DEBUG] testsApi.list for student completed in:`, performance.now() - startTime, "result:", result);
              return result;
            })
        );
        callKeys.push('tests');

        // Homework: backend auto-filters by student's class/section via JWT
        apiCalls.push(safeCall(() => homeworkApi.list()));
        callKeys.push('homework');
      }

      const apiStartTime = performance.now();
      // Progressive loading: load teachers first for staff directory
      let results;
      if (requiredData.teachers && callKeys.includes('teachers')) {
        const teachersIndex = callKeys.indexOf('teachers');
        const teachersCall = apiCalls[teachersIndex];
        const otherCalls = apiCalls.filter((_, index) => index !== teachersIndex);
        const otherKeys = callKeys.filter((_, index) => index !== teachersIndex);
        
        console.log("Loading teachers data first...");
        setTeachersLoading(true);
        const teachersResult = await teachersCall;
        console.log("Teachers data loaded in:", performance.now() - apiStartTime);
        setTeachersLoading(false);
        
        // Update teachers data immediately
        const tempResultsMap = {};
        tempResultsMap.teachers = teachersResult;
        
        // Process teachers data immediately
        const teachersData = teachersResult?.teachers || teachersResult || [];
        if (teachersData.length) {
          const todayStr = new Date().toISOString().slice(0, 10);
          setTeachData(
            teachersData.map((t) => {
              const userObj = t.userId && typeof t.userId === "object" ? t.userId : {};
              const checkedInToday =
                t.checkInDate &&
                new Date(t.checkInDate).toISOString().slice(0, 10) === todayStr;
              
              return {
                ...t,
                id: t._id || t.id,
                userObjectId: userObj._id || (typeof t.userId === "string" ? t.userId : null),
                userId: t.personalEmail || userObj.email || userObj.userId || t._id || "",
                name: t.name || userObj.name || "",
                phone: t.phone || userObj.phone || "",
                email: t.personalEmail || userObj.email || "",
                personalEmail: t.personalEmail || "",
                dob: t.dob ? new Date(t.dob).toISOString().slice(0, 10) : "",
                doj: t.doj ? new Date(t.doj).toISOString().slice(0, 10) : "",
                employmentStatus: t.employmentStatus || userObj.status || "active",
                attendanceStatus: checkedInToday ? "present" : "absent",
                checkInTime: t.checkInTime || null,
                checkInLoc: t.checkInLoc || null,
                checkInDate: t.checkInDate || null,
                role: t.role || "teacher",
                designation: t.designation || "",
                department: t.department || "",
                employmentType: t.employmentType || "",
                assignedClasses: t.teaching?.assignedClasses || [],
                subjects: t.teaching?.subjects || [],
                classTeacherOf: t.teaching?.classTeacherOf || null,
              };
            })
          );
        }
        
        // Load other data in background
        if (otherCalls.length > 0) {
          console.log("Loading other data in background...");
          const otherResults = await Promise.all(otherCalls);
          results = [];
          let resultIndex = 0;
          for (let i = 0; i < callKeys.length; i++) {
            if (callKeys[i] === 'teachers') {
              results[i] = teachersResult;
            } else {
              results[i] = otherResults[resultIndex++];
            }
          }
        } else {
          results = [teachersResult];
        }
      } else {
        results = await Promise.all(apiCalls);
      }
      console.log("All API calls completed in:", performance.now() - apiStartTime);
      
      // Update cache with fresh data
      if (!useCache) {
        results.forEach((result, index) => {
          cache[callKeys[index]] = result;
        });
        cache.timestamp = Date.now();
        cache.userId = user?._id;
      }

      // Process results
      const processingStartTime = performance.now();
      const resultsMap = {};
      callKeys.forEach((key, index) => {
        resultsMap[key] = results[index];
      });
      console.log("Results processing time:", performance.now() - processingStartTime);

      // ── Students ────────────────────────────────────────────────────────────
      console.log("Students API raw result:", resultsMap.students);
      if (requiredData.students && resultsMap.students?.students?.length) {
        console.log("Students count from API:", resultsMap.students.students.length);
        const normalized = resultsMap.students.students.map((s) => ({
          ...s,
          id: s._id || s.id,
          name: s.user?.name || s.name || "",
          userId: s.user?.userId || s.userId || s._id || "",
        }));
        console.log("Normalized students count:", normalized.length);

        // Only fetch attendance if needed and not cached
        if (requiredData.attendance) {
          const today = new Date().toISOString().slice(0, 10);
          let attRes;
          if (useCache && cache.attendance) {
            attRes = cache.attendance;
          } else {
            const attendanceStartTime = performance.now();
            attRes = await safeCall(() => attendanceApi.getTodayStatus({ date: today }));
            console.log(`attendanceApi.getTodayStatus completed in:`, performance.now() - attendanceStartTime);
            cache.attendance = attRes;
          }
          
          const statusMap = attRes?.statuses || {};
          setStuData(
            normalized.map((s) => {
              const attStatus = statusMap[s._id] || statusMap[s.id];
              return attStatus ? { ...s, todayStatus: attStatus } : s;
            })
          );
        } else {
          setStuData(normalized);
        }
      }

      // ── Teachers ────────────────────────────────────────────────────────────
      // Teachers data already processed in progressive loading section above

      // ── Teacher logs ────────────────────────────────────────────────────────
      if (requiredData.logs && resultsMap.logs) {
        // Handle various response formats: { logs: [...] }, { data: [...] }, or [...]
        const logsData = resultsMap.logs.logs || resultsMap.logs.data || resultsMap.logs;
        const logsArray = Array.isArray(logsData) ? logsData : [];
        if (logsArray.length) {
          console.log(`Setting teacher logs: ${logsArray.length} entries`);
          setTeacherLogs(logsArray);
        }
      }

      // ── Timetable ───────────────────────────────────────────────────────────
      if (requiredData.timetable && resultsMap.timetable) {
        const ttRes = resultsMap.timetable;
        if (Array.isArray(ttRes)) {
          const ttObj = {};
          ttRes.forEach(({ class: cls, schedule }) => {
            ttObj[cls] = schedule;
          });
          setTimetable(ttObj);
        } else if (ttRes.schedule) {
          setTimetable({ [ttRes.class]: ttRes.schedule });
        } else {
          setTimetable(ttRes);
        }
      }

      // ── Tests for students ───────────────────────────────────────────────────
      if (user?.role === 'student' && resultsMap.tests) {
        const testsData = Array.isArray(resultsMap.tests) ? resultsMap.tests : resultsMap.tests?.data || resultsMap.tests?.tests || [];
        if (testsData.length) {
          console.log(`[DEBUG] Setting tests for student: ${testsData.length} tests`);
          const normalized = testsData.map((t) => ({
            ...t,
            id: t._id || t.id,
            date: t.date ? t.date.slice(0, 10) : "",
          }));
          setTests(normalized);
        } else {
          console.log("[DEBUG] No tests found for student");
          setTests([]);
        }
      }

      // ── Homework for students ────────────────────────────────────────────────
      if (user?.role === 'student' && resultsMap.homework) {
        const raw = Array.isArray(resultsMap.homework)
          ? resultsMap.homework
          : resultsMap.homework?.homework || resultsMap.homework?.data || [];
        const normalized = raw.map((h) => ({
          ...h,
          id: h._id || h.id,
          _isHomework: true,
          teacher: h.teacherName || (typeof h.teacherId === "object"
            ? (h.teacherId?.name || h.teacherId?.email || "")
            : (h.teacherId || "")),
          subject: typeof h.subject === "object" ? h.subject?.name : (h.subject || ""),
        }));
        setHomework(normalized);
      }

      // ── Patch teacher user doc ───────────────────────────────────────────────
      if (user?.role === "teacher" && requiredData.teachers) {
        const teacherList = resultsMap.teachers?.teachers || [];
        const myTeacher = teacherList.find((t) => {
          const tUserId =
            t.userId && typeof t.userId === "object" ? t.userId._id : t.userId;
          return tUserId === user._id || tUserId === user.id;
        });
        if (myTeacher) {
          const patch = {
            teacherDocId: myTeacher._id,
            classTeacherOf: myTeacher.classTeacherOf || null,
          };
          if (!user.assignedClasses) {
            patch.assignedClasses = myTeacher.assignedClasses || [];
            patch.subject = myTeacher.subject || user.subject;
          }
          patchUserRef.current(patch);
        }
      }
    } finally {
      muteToasts(false);
      setDataLoaded(true);
      fetchInProgressRef.current = false;
      console.log("Total fetchAllData time:", performance.now() - startTime);
    }
  // Only re-create when the logged-in user identity changes, NOT on every render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id, requiredData, isCacheValid]);

  // Function to clear cache and force refresh
  const clearCacheAndRefresh = useCallback(() => {
    cacheRef.current = {
      students: null,
      teachers: null,
      logs: null,
      timetable: null,
      attendance: null,
      timestamp: null,
      userId: null,
    };
    return fetchAllData();
  }, [fetchAllData]);

  return {
    stuData, setStuData,
    teachData, setTeachData,
    curriculum, setCurriculum,
    tests, setTests,
    homework, setHomework,
    teacherLogs, setTeacherLogs,
    timetable, setTimetable,
    dataLoaded,
    teachersLoading,
    fetchAllData,
    clearCacheAndRefresh,
  };
}